import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export interface ReplyAttachment {
  id: number
  file_name: string
  download_url: string
}

export interface Reply {
  id: number
  campaign_id: number | null
  folder: string
  subject: string | null
  html_body: string | null
  text_body: string | null
  is_read: boolean
  is_interested: boolean | null
  is_automated_reply: boolean
  is_tracked_reply: boolean
  date_received: string
  from_name: string | null
  from_email_address: string
  primary_to_email_address: string
  created_at: string
  updated_at: string
  // Flattened lead info
  lead_id: number | null
  lead_email: string | null
  lead_full_name: string | null
  lead_company: string | null
  // Annotated
  emails_sent_count: number | null
  thread_count: number
  thread_id: number | null
  is_flagged: boolean
  // Flattened sender email info
  sender_email_id: number | null
  sender_email_address: string | null
  sender_email_name: string | null
  // Nested
  attachments: ReplyAttachment[]
}

export interface RepliesResponse {
  count: number
  results: Reply[]
}

export interface SyncRepliesResponse {
  synced: number
  created: number
  updated: number
  errors: number
}

export interface ReplyFilters {
  lead_id?: number
  campaign_id?: number
  campaign?: number[]
  search?: string
  sender_email?: string[]
  is_interested?: boolean
  is_read?: boolean
  is_flagged?: boolean
  page?: number
  page_size?: number
}

export interface ReplySummary {
  is_available: boolean
  total: number
  interested: number
  not_interested: number
  flagged: number
}

export interface ReplyToReplyPayload {
  message: string
}

export interface ForwardReplyPayload {
  message: string
  to_emails: { name: string; email_address: string }[]
}

// Query keys
export const replyKeys = {
  all: ['replies'] as const,
  list: (filters: ReplyFilters) => [...replyKeys.all, 'list', filters] as const,
  summary: (filters: ReplyFilters) =>
    [...replyKeys.all, 'summary', filters] as const,
}

export const replyThreadKeys = {
  all: ['reply-threads'] as const,
  list: (filters: ReplyFilters) =>
    [...replyThreadKeys.all, 'list', filters] as const,
  summary: (filters: ReplyFilters) =>
    [...replyThreadKeys.all, 'summary', filters] as const,
}

/** Invalidate both reply and reply-thread caches. */
function invalidateAllReplyCaches(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: replyKeys.all })
  queryClient.invalidateQueries({ queryKey: replyThreadKeys.all })
}

/** Snapshot of all reply/thread caches for rollback on optimistic update failure. */
type ReplySnapshot = [readonly unknown[], unknown][]

/**
 * Optimistically update a single reply across all cache locations
 * (non-paginated lists, thread detail, and infinite query pages).
 * Returns a snapshot of previous data for rollback via restoreReplySnapshot.
 */
function optimisticUpdateReply(
  queryClient: ReturnType<typeof useQueryClient>,
  updateResults: (results: Reply[]) => Reply[]
): ReplySnapshot {
  const snapshot: ReplySnapshot = [
    ...queryClient.getQueriesData<RepliesResponse>({ queryKey: replyKeys.all }),
    ...queryClient.getQueriesData<unknown>({ queryKey: replyThreadKeys.all }),
  ]

  // Non-paginated reply lists + thread detail
  for (const prefix of [
    [...replyKeys.all, 'list'],
    [...replyKeys.all, 'thread'],
  ]) {
    queryClient.setQueriesData<RepliesResponse>({ queryKey: prefix }, (old) => {
      if (!old) return old
      return { ...old, results: updateResults(old.results) }
    })
  }

  // Infinite query pages (replies + threads)
  for (const key of [
    [...replyKeys.all, 'infinite'],
    [...replyThreadKeys.all, 'infinite'],
  ]) {
    queryClient.setQueriesData<InfiniteData<RepliesResponse>>(
      { queryKey: key },
      (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            results: updateResults(page.results),
          })),
        }
      }
    )
  }

  return snapshot
}

/** Restore all reply/thread caches from a snapshot taken before an optimistic update. */
function restoreReplySnapshot(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: ReplySnapshot
) {
  for (const [queryKey, data] of snapshot) {
    queryClient.setQueryData(queryKey, data)
  }
}

// API calls
const getReplies = async (filters: ReplyFilters): Promise<RepliesResponse> => {
  return baseAPICall<RepliesResponse>(() =>
    clientInstance.get('/emailbison/replies/', { params: filters })
  )
}

const syncCampaignReplies = async (
  bisonCampaignId: number
): Promise<SyncRepliesResponse> => {
  return baseAPICall<SyncRepliesResponse>(() =>
    clientInstance.post(`/emailbison/replies/sync/`, null, {
      params: { campaign_id: bisonCampaignId },
    })
  )
}

const getReplySummary = async (
  filters: ReplyFilters
): Promise<ReplySummary> => {
  return baseAPICall<ReplySummary>(() =>
    clientInstance.get('/emailbison/replies/summary/', { params: filters })
  )
}

const getReplyThreads = async (
  filters: ReplyFilters
): Promise<RepliesResponse> => {
  return baseAPICall<RepliesResponse>(() =>
    clientInstance.get('/emailbison/reply-threads/', { params: filters })
  )
}

const getReplyThreadSummary = async (
  filters: ReplyFilters
): Promise<ReplySummary> => {
  return baseAPICall<ReplySummary>(() =>
    clientInstance.get('/emailbison/reply-threads/summary/', {
      params: filters,
    })
  )
}

const replyToReply = async (
  replyId: number,
  payload: ReplyToReplyPayload
): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.post(`/emailbison/replies/${replyId}/reply/`, payload)
  )
}

const forwardReply = async (
  replyId: number,
  payload: ForwardReplyPayload
): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.post(`/emailbison/replies/${replyId}/forward/`, payload)
  )
}

const markReplyRead = async (
  replyId: number,
  isRead: boolean
): Promise<Reply> => {
  return baseAPICall<Reply>(() =>
    clientInstance.patch(`/emailbison/replies/${replyId}/`, {
      is_read: isRead,
    })
  )
}

// Hooks
const REPLY_PAGE_SIZE = 25

export const useGetReplies = (filters: ReplyFilters = {}) => {
  return useQuery({
    queryKey: replyKeys.list(filters),
    queryFn: () => getReplies(filters),
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
    placeholderData: (prev) => prev,
  })
}

export const useGetRepliesInfinite = (filters: ReplyFilters = {}) => {
  return useInfiniteQuery({
    queryKey: [...replyKeys.all, 'infinite', filters],
    queryFn: ({ pageParam }) =>
      getReplies({ ...filters, page: pageParam, page_size: REPLY_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      return totalFetched < lastPage.count ? allPages.length + 1 : undefined
    },
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
  })
}

export const useGetReplyThreadsInfinite = (filters: ReplyFilters = {}) => {
  return useInfiniteQuery({
    queryKey: [...replyThreadKeys.all, 'infinite', filters],
    queryFn: ({ pageParam }) =>
      getReplyThreads({
        ...filters,
        page: pageParam,
        page_size: REPLY_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      return totalFetched < lastPage.count ? allPages.length + 1 : undefined
    },
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
  })
}

export const useGetReplyThreadSummary = (filters: ReplyFilters = {}) => {
  return useQuery({
    queryKey: replyThreadKeys.summary(filters),
    queryFn: () => getReplyThreadSummary(filters),
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
    placeholderData: (prev) => prev,
  })
}

export const useGetThreadReplies = (
  leadId: number | null,
  campaignId: number | null
) => {
  return useQuery({
    queryKey: [...replyKeys.all, 'thread', leadId, campaignId],
    queryFn: () => getReplies({ lead_id: leadId!, campaign_id: campaignId! }),
    enabled: !!leadId && !!campaignId,
  })
}

export const useMarkReplyInterested = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      replyId,
      isInterested,
    }: {
      replyId: number
      isInterested: boolean
    }) =>
      baseAPICall<Reply>(() =>
        clientInstance.post(`/emailbison/replies/${replyId}/mark-interested/`, {
          is_interested: isInterested,
        })
      ),

    onMutate: ({ replyId, isInterested }) => {
      const snapshot = optimisticUpdateReply(queryClient, (results) =>
        results.map((r) =>
          r.id === replyId ? { ...r, is_interested: isInterested } : r
        )
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) restoreReplySnapshot(queryClient, context.snapshot)
    },

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useMarkReplyAutomated = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      replyId,
      isAutomated,
    }: {
      replyId: number
      isAutomated: boolean
    }) =>
      baseAPICall<Reply>(() =>
        clientInstance.patch(`/emailbison/replies/${replyId}/`, {
          is_automated_reply: isAutomated,
        })
      ),

    onMutate: ({ replyId, isAutomated }) => {
      const snapshot = optimisticUpdateReply(queryClient, (results) =>
        results.map((r) =>
          r.id === replyId ? { ...r, is_automated_reply: isAutomated } : r
        )
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) restoreReplySnapshot(queryClient, context.snapshot)
    },

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useUnsubscribeReplyContact = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (replyId: number) =>
      baseAPICall<{ success: boolean }>(() =>
        clientInstance.post(`/emailbison/replies/${replyId}/unsubscribe/`)
      ),

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useDeleteReply = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (replyId: number) =>
      baseAPICall<{ success: boolean }>(() =>
        clientInstance.delete(`/emailbison/replies/${replyId}/delete/`)
      ),

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useToggleThreadFlag = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (threadId: number) =>
      baseAPICall<{ is_flagged: boolean }>(() =>
        clientInstance.post(
          `/emailbison/reply-threads/${threadId}/toggle-flag/`
        )
      ),

    onMutate: (threadId) => {
      const snapshot = queryClient.getQueriesData({
        queryKey: replyThreadKeys.all,
      })
      queryClient.setQueriesData<InfiniteData<RepliesResponse>>(
        { queryKey: [...replyThreadKeys.all, 'infinite'] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((r) =>
                r.thread_id === threadId
                  ? { ...r, is_flagged: !r.is_flagged }
                  : r
              ),
            })),
          }
        }
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          queryClient.setQueryData(key, data)
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: replyThreadKeys.all })
    },
  })
}

export const useGetReplySummary = (filters: ReplyFilters = {}) => {
  return useQuery({
    queryKey: replyKeys.summary(filters),
    queryFn: () => getReplySummary(filters),
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
    placeholderData: (prev) => prev,
  })
}

export const useSyncCampaignReplies = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => syncCampaignReplies(bisonCampaignId),
    onSuccess: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useReplyToReply = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      replyId,
      payload,
    }: {
      replyId: number
      payload: ReplyToReplyPayload
    }) => replyToReply(replyId, payload),
    onSuccess: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useForwardReply = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      replyId,
      payload,
    }: {
      replyId: number
      payload: ForwardReplyPayload
    }) => forwardReply(replyId, payload),
    onSuccess: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useMarkReplyRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ replyId, isRead }: { replyId: number; isRead: boolean }) =>
      markReplyRead(replyId, isRead),

    onMutate: ({ replyId, isRead }) => {
      const snapshot = optimisticUpdateReply(queryClient, (results) =>
        results.map((r) => (r.id === replyId ? { ...r, is_read: isRead } : r))
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) restoreReplySnapshot(queryClient, context.snapshot)
    },

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}
